// Cajón del cliente: encabezado, contacto y pestañas Productos · Vendedores · Tiendas · Referidos.
// Las tres primeras salen del historial de compras. La pestaña activa vive en la URL (`?tab=`).
import { CUSTOMER_FALLBACK_NAME } from '@/shared/lib/format/customerMention';
import { useMemo, useState, type ComponentType } from 'react';
import { CreditCard, LayoutGrid, Mail, Phone, Users } from 'lucide-react';
import { Button, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { daysSinceMx, formatDateMx } from '@/lib/dateHelpers';
import type {
    BackofficeCustomerDetail,
    BackofficeCustomerPurchase,
} from '@/api/services/retail-customer/backoffice';
import { formatPhone } from '@/shared/lib/format/phone';
import { plural } from '@/shared/lib/format/plural';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { ContactCard } from '@/shared/patterns/ContactCard';
import { DetailCard, DetailTabs } from '@/shared/patterns/DetailCard';
import { DetailHeader, DetailHeaderPill } from '@/shared/patterns/DetailHeader';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { STATUS_PILL_TONE_CLASS, StatusPill, tierFullLabel } from '@/shared/patterns/Pills';
import { CustomerReferralsTab, useCustomerReferrals } from '@/shared/patterns/CustomerReferrals';
import {
    CustomerMessagesTab,
    MessageMomentCategoryEnum,
    useCanSeeCustomerMessages,
} from '@/shared/patterns/CustomerMessages';
import {
    CATEGORY_ICON,
    CategoryIcon,
    SaleTicketProduct,
    ticketTitle,
    useTicketExpansion,
} from '@/shared/patterns/SaleTicket';

import {
    byStore,
    bySeller,
    categoryMix,
    distinctSkus,
    mixTicketCategory,
    purchasesInMix,
    sortBy,
    ticketRowName,
    toggleSort,
    toTicketRow,
    type Breakdown,
    type CustomerTicketRow,
    type MixKey,
    type MixSlice,
    type TableSort,
    uncategorizedSales,
} from '../lib/customerDetail';
import {
    statusOf,
    type CreditReadState,
    type CustomerCredit,
    type CustomerStatus,
} from '../lib/customersList';
import {
    ACCOUNT_ONLY_NOTE,
    AGGREGATES_NO_SOURCE,
    daysLabel,
    EMAIL_NO_SOURCE,
    LEVEL_ERROR,
    LEVEL_FORBIDDEN,
    money,
    paymentLabel,
    PREPAID_NO_CATEGORY,
    STATUS_HINT,
    STATUS_LABEL,
    STATUS_TONE,
} from '../lib/customersFormat';
import { Missing, WrapLabel } from './cells';

export const DETAIL_TABS = ['productos', 'vendedores', 'tiendas', 'referidos', 'mensajes'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

/** Un `?tab=` que no es de esta pantalla cae en «productos». */
export function parseDetailTab(raw: string | undefined): DetailTab {
    return DETAIL_TABS.includes(raw as DetailTab) ? (raw as DetailTab) : 'productos';
}

type IconType = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

const MIX_LABEL: Record<MixKey, string> = {
    ALL: 'Todos',
    PHONES: 'Equipos',
    SIMS: 'SIMs',
    ACCESSORIES: 'Accesorios',
    SERVICES: 'Servicios',
    PREPAID: 'Tarjetas / Prepago',
};

const MIX_ICON: Record<MixKey, IconType> = {
    ALL: LayoutGrid,
    PHONES: CATEGORY_ICON.PHONE,
    SIMS: CATEGORY_ICON.SIM,
    ACCESSORIES: CATEGORY_ICON.ACCESSORY,
    SERVICES: CATEGORY_ICON.SERVICE,
    PREPAID: CreditCard,
};

function MixCard({
    slice,
    active,
    onClick,
}: {
    slice: MixSlice;
    active: boolean;
    onClick: () => void;
}) {
    const Icon = MIX_ICON[slice.key];
    const unsourced = slice.salesCount === null;
    const empty = slice.salesCount === 0;
    return (
        <button
            type="button"
            aria-pressed={active}
            aria-disabled={unsourced || undefined}
            title={unsourced ? PREPAID_NO_CATEGORY : undefined}
            onClick={() => {
                if (!unsourced) onClick();
            }}
            className={cn(
                'flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-1.5 text-left transition-colors',
                active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-accent/50',
                (empty || unsourced) && 'opacity-50',
                unsourced && 'cursor-help hover:bg-card',
            )}
        >
            <Icon className="size-4 flex-none text-muted-foreground" aria-hidden />
            <span className="min-w-0">
                <span className="block text-[9px] font-bold uppercase leading-tight tracking-[0.05em] text-muted-foreground">
                    {MIX_LABEL[slice.key]}
                </span>
                <span className="block text-[13px] font-bold leading-tight tabular-nums">
                    {slice.salesCount ?? '—'}{' '}
                    <span className="text-[9.5px] font-normal text-muted-foreground">
                        {slice.salesCount === 1 ? 'venta' : 'ventas'}
                    </span>
                </span>
                <span className="block truncate text-[10px] tabular-nums text-muted-foreground">
                    {slice.amountCents === null ? '—' : money(slice.amountCents)}
                </span>
            </span>
        </button>
    );
}

type PurchaseSortKey = 'date' | 'product' | 'store' | 'payment' | 'amount';
type BreakdownSortKey = 'name' | 'store' | 'count' | 'amount' | 'pct' | 'last';

function purchaseValue(p: CustomerTicketRow, key: PurchaseSortKey, mix: MixKey): string | number {
    switch (key) {
        case 'date':
            return p.soldAt;
        case 'product':
            return ticketRowName(p, mix);
        case 'store':
            return p.storeName ?? '';
        case 'payment':
            return paymentLabel(p.payment);
        case 'amount':
            return p.amountCents;
    }
}

function breakdownValue(b: Breakdown, key: BreakdownSortKey): string | number {
    switch (key) {
        case 'name':
            return b.name;
        case 'store':
            return b.storeName ?? '';
        case 'count':
            return b.count;
        case 'amount':
            return b.amountCents;
        case 'pct':
            return b.pct;
        case 'last':
            return b.lastAt;
    }
}

/** Título de la celda: todas las líneas del ticket y su folio. */
function productTitle(p: CustomerTicketRow): string {
    return [ticketTitle(p, p.productName), p.folio ? `Folio ${p.folio}` : null]
        .filter(Boolean)
        .join(' · ');
}

function purchaseColumns(mix: MixKey): DataTableColumn<CustomerTicketRow, PurchaseSortKey>[] {
    const category = mixTicketCategory(mix);
    return [
        {
            key: 'date',
            label: 'Fecha',
            width: '80px',
            sortKey: 'date',
            cellClassName: () => 'tabular-nums text-[10.5px] text-muted-foreground',
            cell: (p) => formatDateMx(p.soldAt),
        },
        {
            key: 'product',
            label: 'Producto',
            sortKey: 'product',
            cellOverflow: 'wrap',
            cellTitle: productTitle,
            cell: (p) => (
                <span className="font-medium leading-tight">
                    <SaleTicketProduct
                        sale={p}
                        category={category}
                        saleName={p.productName}
                        saleIcon={p.category ? <CategoryIcon category={p.category} /> : undefined}
                        showSku
                    />
                </span>
            ),
        },
        {
            key: 'store',
            label: 'Tienda',
            width: '62px',
            sortKey: 'store',
            cellOverflow: 'wrap',
            cellClassName: () => 'text-[10.5px] leading-tight',
            cellTitle: (p) => p.storeName ?? undefined,
            cell: (p) => p.storeName ?? '—',
        },
        {
            key: 'payment',
            label: <WrapLabel>Forma de pago</WrapLabel>,
            width: '70px',
            sortKey: 'payment',
            cellOverflow: 'nowrap',
            cell: (p) => (
                <StatusPill
                    tone={
                        p.payment === 'SUREKEEP'
                            ? 'success'
                            : p.payment === 'CASH'
                              ? 'neutral'
                              : 'info'
                    }
                >
                    {paymentLabel(p.payment)}
                </StatusPill>
            ),
        },
        {
            key: 'amount',
            label: 'Monto',
            width: '80px',
            numeric: true,
            sortKey: 'amount',
            cellClassName: () => 'text-[11px] font-semibold',
            cell: (p) => money(p.amountCents),
        },
    ];
}

function breakdownColumns(withStore: boolean): DataTableColumn<Breakdown, BreakdownSortKey>[] {
    const columns: (DataTableColumn<Breakdown, BreakdownSortKey> | null)[] = [
        {
            key: 'name',
            label: withStore ? 'Vendedor' : 'Tienda',
            sortKey: 'name',
            cellOverflow: 'wrap',
            cellTitle: (b) => b.name,
            cellClassName: () => 'font-medium leading-tight',
            cell: (b) => b.name,
        },
        withStore
            ? {
                  key: 'store',
                  label: 'Tienda',
                  width: '56px',
                  sortKey: 'store',
                  cellOverflow: 'wrap',
                  cellClassName: () => 'text-[10.5px] leading-tight',
                  cellTitle: (b) => b.storeName ?? undefined,
                  cell: (b) => b.storeName ?? '—',
              }
            : null,
        {
            key: 'count',
            label: withStore ? 'Ventas' : 'Compras',
            width: withStore ? '52px' : '66px',
            numeric: true,
            sortKey: 'count',
            cellClassName: () => 'font-semibold',
            cell: (b) => b.count,
        },
        {
            key: 'amount',
            label: 'Total $',
            width: '76px',
            numeric: true,
            sortKey: 'amount',
            cellClassName: () => 'text-[10.5px] font-semibold',
            cell: (b) => money(b.amountCents),
        },
        {
            key: 'pct',
            label: '%',
            width: '46px',
            numeric: true,
            sortKey: 'pct',
            cellClassName: () => 'text-[10.5px] text-muted-foreground',
            cell: (b) => `${String(b.pct)}%`,
        },
        {
            key: 'last',
            label: <WrapLabel>{withStore ? 'Última venta' : 'Última compra'}</WrapLabel>,
            width: '80px',
            sortKey: 'last',
            cellClassName: () => 'tabular-nums text-[10px] text-muted-foreground',
            cell: (b) => formatDateMx(b.lastAt),
        },
    ];
    return columns.filter((c): c is DataTableColumn<Breakdown, BreakdownSortKey> => c !== null);
}

const SELLER_COLUMNS = breakdownColumns(true);
const STORE_COLUMNS = breakdownColumns(false);
const TEXT_PURCHASE_KEYS: readonly PurchaseSortKey[] = ['product', 'store', 'payment'];
const TEXT_BREAKDOWN_KEYS: readonly BreakdownSortKey[] = ['name', 'store'];
const DEFAULT_PURCHASE_SORT: TableSort<PurchaseSortKey> = { key: 'date', direction: 'desc' };
const DEFAULT_BREAKDOWN_SORT: TableSort<BreakdownSortKey> = { key: 'amount', direction: 'desc' };

/** Filtro del mix y órdenes de las tablas, atados al cliente que se está viendo. */
interface DetailView {
    customerId: string | undefined;
    mix: MixKey;
    purchaseSort: TableSort<PurchaseSortKey>;
    sellerSort: TableSort<BreakdownSortKey>;
    storeSort: TableSort<BreakdownSortKey>;
}

const DEFAULT_VIEW: Omit<DetailView, 'customerId'> = {
    mix: 'ALL',
    purchaseSort: DEFAULT_PURCHASE_SORT,
    sellerSort: DEFAULT_BREAKDOWN_SORT,
    storeSort: DEFAULT_BREAKDOWN_SORT,
};

function levelBadge(credit: CustomerCredit | undefined, state: CreditReadState) {
    if (credit !== undefined) {
        return (
            <DetailHeaderPill tone="neutral">
                Nivel SK · {tierFullLabel(credit.level)}
            </DetailHeaderPill>
        );
    }
    const reason =
        state === 'forbidden' ? LEVEL_FORBIDDEN : state === 'error' ? LEVEL_ERROR : undefined;
    return (
        <DetailHeaderPill tone="neutral" title={reason}>
            Nivel SK · —
        </DetailHeaderPill>
    );
}

export interface CustomerDetailPanelProps {
    customer: BackofficeCustomerDetail | undefined;
    purchases: BackofficeCustomerPurchase[] | undefined;
    isLoadingMorePurchases: boolean;
    purchasesTruncated: boolean;
    credit: CustomerCredit | undefined;
    creditState: CreditReadState;
    /** La fila dice que abrió su cuenta PCF y no ha comprado. */
    accountOnly?: boolean;
    tab: DetailTab;
    onTabChange: (tab: DetailTab) => void;
    isLoading: boolean;
    isLoadingPurchases: boolean;
    error: Error | null;
    purchasesError: Error | null;
    onRetry: () => void;
    hasSelection: boolean;
}

export function CustomerDetailPanel({
    customer,
    purchases,
    isLoadingMorePurchases,
    purchasesTruncated,
    credit,
    creditState,
    accountOnly = false,
    tab: requestedTab,
    onTabChange,
    isLoading,
    isLoadingPurchases,
    error,
    purchasesError,
    onRetry,
    hasSelection,
}: CustomerDetailPanelProps) {
    const customerId = customer?.retailCustomerId;
    // La cadena la pone el token del Admin VL: sin `?value=`.
    const referrals = useCustomerReferrals(customerId ?? null);
    const canSeeMessages = useCanSeeCustomerMessages(MessageMomentCategoryEnum.SALE);
    // Un `?tab=mensajes` sin permiso cae en la pestaña por defecto en vez de dejar el cajón vacío.
    const tab = requestedTab === 'mensajes' && !canSeeMessages ? 'productos' : requestedTab;
    const [view, setView] = useState<DetailView>({ ...DEFAULT_VIEW, customerId });
    // Al cambiar de cliente el filtro de categoría y los órdenes vuelven a su default.
    const current = view.customerId === customerId ? view : { ...DEFAULT_VIEW, customerId };
    const { mix, purchaseSort, sellerSort, storeSort } = current;
    const patchView = (partial: Partial<DetailView>) => {
        setView({ ...current, ...partial });
    };
    const setMix = (next: MixKey) => {
        patchView({ mix: next });
    };
    const history = useMemo(() => purchases ?? [], [purchases]);
    const mixSlices = useMemo(() => categoryMix(history), [history]);
    const sellers = useMemo(() => bySeller(history), [history]);
    const stores = useMemo(() => byStore(history), [history]);
    const skuCount = useMemo(() => distinctSkus(history), [history]);
    const uncategorized = useMemo(() => uncategorizedSales(history), [history]);
    const { expandColumn, tableProps } = useTicketExpansion<CustomerTicketRow, PurchaseSortKey>({
        category: mixTicketCategory(mix),
        totalCentsOf: (p) => p.amountCents,
    });

    if (!hasSelection) {
        return (
            <MasterDetailEmpty
                icon={<Users className="size-8" aria-hidden />}
                title="Selecciona un cliente"
                sub="Elige una fila de la tabla para ver su comportamiento en tus tiendas."
            />
        );
    }

    if (error) {
        return (
            <UnavailableState
                title="No se pudo cargar la ficha del cliente"
                body={error.message}
                action={
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        Reintentar
                    </Button>
                }
            />
        );
    }

    if (isLoading || !customer) {
        return (
            <div className="flex flex-col gap-3 p-3.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-56" />
                <Skeleton lines={6} />
            </div>
        );
    }

    const historyComplete =
        purchases !== undefined && !isLoadingMorePurchases && !purchasesTruncated;
    const purchaseCount = customer.purchaseCount ?? (historyComplete ? history.length : null);
    const totalCents =
        customer.totalSpentCents ??
        (historyComplete ? history.reduce((sum, p) => sum + p.amountCents, 0) : null);
    const ticketCents =
        purchaseCount !== null && purchaseCount > 0 && totalCents !== null
            ? Math.round(totalCents / purchaseCount)
            : null;
    const days = customer.lastPurchaseAt === null ? null : daysSinceMx(customer.lastPurchaseAt);
    const status: CustomerStatus | null = accountOnly
        ? 'ACCOUNT_ONLY'
        : statusOf(days, purchaseCount);
    const aggregatesReason = customer.aggregatesUnavailableReason ?? AGGREGATES_NO_SOURCE;
    const counting = isLoadingPurchases || isLoadingMorePurchases;
    const tabCount = (n: number) => (counting ? '' : ` ${String(n)}`);

    const visiblePurchases = sortBy(
        purchasesInMix(history, mix).map(toTicketRow),
        purchaseSort,
        (p, key) => purchaseValue(p, key, mix),
    );
    const purchaseColumnsShown = [expandColumn, ...purchaseColumns(mix)];
    const shownSellers = sortBy(sellers, sellerSort, breakdownValue);
    const shownStores = sortBy(stores, storeSort, breakdownValue);

    const historyError = purchasesError ? (
        <UnavailableState
            title="No se pudo cargar el historial"
            body={purchasesError.message}
            action={
                <Button variant="outline" size="sm" onClick={onRetry}>
                    Reintentar
                </Button>
            }
        />
    ) : null;
    const truncatedNote = purchasesTruncated ? (
        <span role="status">
            <span className="font-semibold">Este historial no está completo.</span> El sistema
            seguía ofreciendo más compras de las que se pudieron traer.
        </span>
    ) : undefined;

    return (
        <>
            <DetailHeader
                ariaLabel="Encabezado del cliente"
                name={customer.name ?? <Missing reason="La venta no congeló el nombre." />}
                badges={
                    <>
                        {status === null ? null : (
                            <StatusPill tone={STATUS_TONE[status]} title={STATUS_HINT[status]}>
                                {STATUS_LABEL[status]}
                            </StatusPill>
                        )}
                        {levelBadge(credit, creditState)}
                    </>
                }
                subItems={[
                    customer.firstPurchaseAt === null
                        ? null
                        : `cliente desde ${formatDateMx(customer.firstPurchaseAt)}`,
                ]}
                idLabel="Cliente"
                idValue={customer.retailCustomerId}
                factColumns={2}
                facts={[
                    {
                        key: 'purchases',
                        label: 'Compras',
                        value: accountOnly
                            ? 0
                            : (purchaseCount ?? <Missing reason={aggregatesReason} />),
                    },
                    {
                        key: 'total',
                        label: 'Total gastado',
                        value: accountOnly ? (
                            '—'
                        ) : totalCents === null ? (
                            <Missing reason={aggregatesReason} />
                        ) : (
                            money(totalCents)
                        ),
                    },
                    {
                        key: 'ticket',
                        label: 'Ticket promedio',
                        value: accountOnly ? (
                            '—'
                        ) : ticketCents === null ? (
                            <Missing reason={aggregatesReason} />
                        ) : (
                            money(ticketCents)
                        ),
                    },
                    {
                        key: 'days',
                        label: 'Días s/compra',
                        title:
                            customer.lastPurchaseAt === null
                                ? undefined
                                : `Última compra: ${formatDateMx(customer.lastPurchaseAt)}`,
                        value: days === null ? '—' : daysLabel(days),
                    },
                ]}
            />

            {accountOnly ? (
                <p
                    role="note"
                    className={cn(
                        'mx-4 mt-2 flex-none rounded-md border px-2.5 py-1.5 text-[11px] font-medium',
                        STATUS_PILL_TONE_CLASS.info,
                    )}
                >
                    {ACCOUNT_ONLY_NOTE}.
                </p>
            ) : null}

            <div className="flex flex-none gap-2 border-b border-border px-4 py-2">
                <ContactCard
                    icon={Phone}
                    label="Teléfono"
                    value={customer.phone ? formatPhone(customer.phone) : null}
                    copyValue={customer.phone}
                    missing={<Missing reason="La venta no congeló el teléfono." />}
                    className={customer.phone ? 'flex-[3]' : 'flex-[2]'}
                />
                <ContactCard
                    icon={Mail}
                    label="Correo"
                    value={customer.email || null}
                    copyValue={customer.email}
                    missing={<Missing reason={EMAIL_NO_SOURCE} />}
                    className={customer.email ? 'flex-[3]' : 'flex-[2]'}
                />
            </div>

            <DetailTabs
                tabs={[
                    { key: 'productos', label: `Productos${tabCount(skuCount)}` },
                    { key: 'vendedores', label: `Vendedores${tabCount(sellers.length)}` },
                    { key: 'tiendas', label: `Tiendas${tabCount(stores.length)}` },
                    {
                        key: 'referidos',
                        label: `Referidos${referrals.data ? ` ${String(referrals.data.referrals.length)}` : ''}`,
                    },
                    ...(canSeeMessages ? [{ key: 'mensajes' as const, label: 'Mensajes' }] : []),
                ]}
                active={tab}
                onChange={onTabChange}
            />

            <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
                {tab === 'productos' ? (
                    <>
                        <DetailCard
                            title="Mix por categoría"
                            footnote={
                                uncategorized > 0
                                    ? `${plural(uncategorized, 'venta no tiene', 'ventas no tienen')} categoría en su detalle (kit o venta anterior al detalle por línea): solo cuenta en «Todos».`
                                    : undefined
                            }
                        >
                            <div className="grid grid-cols-3 gap-1.5">
                                {mixSlices.map((slice) => (
                                    <MixCard
                                        key={slice.key}
                                        slice={slice}
                                        active={mix === slice.key}
                                        onClick={() => {
                                            setMix(slice.key);
                                        }}
                                    />
                                ))}
                            </div>
                        </DetailCard>
                        <DetailCard
                            title={`Compras${mix === 'ALL' ? '' : ` · ${MIX_LABEL[mix]}`} · ${plural(visiblePurchases.length, 'ticket', 'tickets')}`}
                            flush
                            meta={
                                mix === 'ALL' ? (
                                    plural(skuCount, 'SKU distinto', 'SKUs distintos')
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMix('ALL');
                                        }}
                                        className="text-[10.5px] text-muted-foreground hover:text-foreground"
                                    >
                                        × quitar filtro
                                    </button>
                                )
                            }
                            footnote={truncatedNote}
                        >
                            {historyError ?? (
                                <DataTable
                                    columns={purchaseColumnsShown}
                                    rows={visiblePurchases}
                                    rowKey={(p) => p.saleId}
                                    {...tableProps}
                                    sort={purchaseSort}
                                    onSortToggle={(key) => {
                                        patchView({
                                            purchaseSort: toggleSort(
                                                purchaseSort,
                                                key,
                                                TEXT_PURCHASE_KEYS,
                                            ),
                                        });
                                    }}
                                    isLoading={isLoadingPurchases}
                                    skeletonRows={4}
                                    empty="Sin compras registradas."
                                    caption="Compras del cliente con fecha, producto, tienda, forma de pago y monto."
                                    density="compact"
                                />
                            )}
                        </DetailCard>
                    </>
                ) : null}

                {tab === 'vendedores' ? (
                    <DetailCard
                        title="Vendedores que lo atienden"
                        flush
                        meta={counting ? '—' : plural(sellers.length, 'vendedor', 'vendedores')}
                        footnote={truncatedNote}
                    >
                        {historyError ?? (
                            <DataTable
                                columns={SELLER_COLUMNS}
                                rows={shownSellers}
                                rowKey={(b) => b.key}
                                sort={sellerSort}
                                onSortToggle={(key) => {
                                    patchView({
                                        sellerSort: toggleSort(
                                            sellerSort,
                                            key,
                                            TEXT_BREAKDOWN_KEYS,
                                        ),
                                    });
                                }}
                                isLoading={isLoadingPurchases}
                                skeletonRows={3}
                                empty="Sin vendedor asignado."
                                caption="Vendedores que atendieron al cliente con tienda, ventas, total, porcentaje y última venta."
                                density="compact"
                            />
                        )}
                    </DetailCard>
                ) : null}

                {tab === 'tiendas' ? (
                    <DetailCard
                        title="Tiendas donde compra"
                        flush
                        meta={counting ? '—' : plural(stores.length, 'tienda', 'tiendas')}
                        footnote={truncatedNote}
                    >
                        {historyError ?? (
                            <DataTable
                                columns={STORE_COLUMNS}
                                rows={shownStores}
                                rowKey={(b) => b.key}
                                sort={storeSort}
                                onSortToggle={(key) => {
                                    patchView({
                                        storeSort: toggleSort(storeSort, key, TEXT_BREAKDOWN_KEYS),
                                    });
                                }}
                                isLoading={isLoadingPurchases}
                                skeletonRows={2}
                                empty="Sin desglose por tienda."
                                caption="Tiendas donde compra el cliente con compras, total, porcentaje y última compra."
                                density="compact"
                            />
                        )}
                    </DetailCard>
                ) : null}

                {tab === 'referidos' ? (
                    <CustomerReferralsTab
                        retailCustomerId={customer.retailCustomerId}
                        customerName={customer.name ?? CUSTOMER_FALLBACK_NAME}
                        customerPhone={customer.phone}
                    />
                ) : null}

                {tab === 'mensajes' ? (
                    <CustomerMessagesTab
                        retailCustomerId={customer.retailCustomerId}
                        category={MessageMomentCategoryEnum.SALE}
                    />
                ) : null}
            </div>
        </>
    );
}
